import { useCallback } from "react";
import { JNode } from "$components/JTree/JTree";
import ReceptionLabel from "./ReceptionLabel";
import ReceptionReferenceLabel from "./ReceptionReferenceLabel";
import { EDB, ProductQuantity, Reception } from "$types";
import { ReceptionReference } from "$types/ReceptionReference";
import ProductQuantityLabel from "./ProductQuantityLabel";
import ReferenceType from "$types/ReferenceType";
import { EDBIntegratedOrders } from "$types/EDBIntegratedOrders";

const getPackWithTooMuchProductsMessage = (
  remainers: RemainingProductQuantity[]
): string => {
  const firstPart =
    remainers.length > 1
      ? "Plusieurs produits dans la référence de mise en stock ne sont pas demandés : "
      : "Un produit dans la référence de mise en stock n'est pas demandé : ";
  return (
    firstPart +
    remainers
      .map((r) => `\n- ${r.productReference} (+${r.remainingQuantity})`)
      .join("")
  );
};
const getNoCheckableMessage = (): string =>
  "Aucune référence de mise en stock n'est sélectionnable pour cette réception.";

const getNoReceptionReferencesMessage = (): string =>
  "Aucun référencement n'est sélectionnable ou n'a été effectué pour cette réception.";

// =====

type RemainingProductQuantity = {
  productReference: string;
  remainingQuantity: number;
  suborderReference: string;
};

export type TreeReference = {
  productQuantity: ProductQuantity;
  productQuantities: ProductQuantity[];
  reception: Reception;
  receptionReference: ReceptionReference;
};

/**
 * CheckboxTree stores selection state as stringified `value`s.
 * Embedding the full reception (~1MB with nested orders/files) in every node
 * multiplies memory by the number of lines and freezes/crashes the tab (OOM).
 * Keep only fields needed by "Ajouter au suivi" (Header.tsx).
 */
const toSelectableReception = (reception: Reception) => ({
  id: reception.id,
  company: reception.company,
  warehouse: reception.warehouse,
  reference: reception.reference,
});

const toSelectableReceptionReference = (
  receptionReference: ReceptionReference
) => ({
  id: receptionReference.id,
  referenceType: receptionReference.referenceType,
  productReference: receptionReference.productReference,
  originalProductReference: receptionReference.originalProductReference,
  suborderReferences: receptionReference.suborderReferences,
  quantity: receptionReference.quantity,
  productId: receptionReference.productId,
});

const toSelectableProductQuantity = (productQuantity: ProductQuantity) => ({
  productId: productQuantity.productId,
  receivedQuantity: productQuantity.receivedQuantity,
  suborderReference: productQuantity.suborderReference,
  orderReference: productQuantity.orderReference,
  suborderProductId: (productQuantity as any).suborderProductId,
});

const toTreeReferenceValue = (treeReference: TreeReference): string =>
  JSON.stringify({
    reception: toSelectableReception(treeReference.reception),
    productQuantity: treeReference.productQuantity
      ? toSelectableProductQuantity(treeReference.productQuantity)
      : null,
    productQuantities: (treeReference.productQuantities || []).map(
      toSelectableProductQuantity
    ),
    receptionReference: toSelectableReceptionReference(
      treeReference.receptionReference
    ),
  });

type Props = {
  hideNotIntegratedProductReferences?: boolean;
  integratedOrders?: EDBIntegratedOrders;
  edbProducts?: EDB["products"];
  onEdit?: () => Promise<void> | void;
  showCheckbox?: boolean;
  showCheckboxForProductQuantity?: boolean;
  showCheckboxForReceptionWithoutProducts?: boolean;
};

/**
 * A reception tree can have 2 shapes:
 * ORDER REFERENCE
 * - A reception
 *    - A reception reference
 *      - A product quantity <-- not checkable: pick the whole order reference
 *
 * PRODUCT REFERENCE
 * - A reception
 *  - A reception reference (product reference)
 *
 * If an order reference has more products than in an integrated order (if provided), it is disabled.
 */
const useReceptionForTree = ({
  hideNotIntegratedProductReferences = true,
  integratedOrders,
  edbProducts,
  onEdit,
  showCheckbox = false,
  showCheckboxForProductQuantity = false,
  showCheckboxForReceptionWithoutProducts = false,
}: Props) => {
  const formatProductQuantity = useCallback(
    (
      reception: Reception,
      productQuantity: ProductQuantity,
      receptionReference: ReceptionReference
    ): JNode => {
      return {
        value: toTreeReferenceValue({
          reception,
          productQuantity,
          productQuantities: [productQuantity],
          receptionReference,
        }),
        label: (
          <ProductQuantityLabel
            productQuantity={productQuantity}
            receptionReference={receptionReference}
            reception={reception}
            onEdit={onEdit}
          />
        ),
        showCheckbox: showCheckboxForProductQuantity,
      };
    },
    [onEdit, showCheckboxForProductQuantity]
  );

  const formatOrderReference = useCallback(
    (
      reception: Reception,
      productQuantities: ProductQuantity[],
      receptionReference: ReceptionReference
    ): JNode | null => {
      // Check if this order reference is already tracked in edbProducts
      // For ORDER references, if any product is tracked (has matching receptionReferenceId),
      // the entire order reference is considered tracked and should not be displayed
      if (edbProducts) {
        const alreadyTrackedProduct = (edbProducts as any[]).find(
          (p) => p.receptionReferenceId === receptionReference.id
        );

        // If found, it means all products in this order reference are tracked
        // Don't check quantities - just check if it exists
        if (alreadyTrackedProduct) {
          return null;
        }
      }

      // Filter out products with zero received quantity
      const filteredProductQuantities = productQuantities.filter(
        (pq) => pq.receivedQuantity > 0
      );

      const children = filteredProductQuantities.map((pq) =>
        formatProductQuantity(reception, pq, receptionReference)
      );

      let disabled = false;
      let disabledMessage: string;
      if (integratedOrders) {
        /* Check if an orderReference covers the integratedOrders needs.
      If the orderReference has more products than in the integratedOrders, it's disabled

      Example: 
        - integratedOrders asked for 10A and 5B
        - orderReference is a pack of 10A, 2B and 3C => it can't be picked because 3C were not integrated.
      */
        const remainingProductQuantities: RemainingProductQuantity[] =
          productQuantities.map((pq) => ({
            productReference: pq.productId,
            remainingQuantity: pq.receivedQuantity,
            suborderReference: pq.suborderReference,
          }));

        integratedOrders.orderProducts.forEach((orderProduct) => {
          const { order, quantity, suborderId, suborderProductId } =
            orderProduct;
          const suborder = order.suborders.find((s) => s.id === suborderId);
          const { reference } = suborder.suborderProducts.find(
            (p) => p.id === suborderProductId
          );
          const productIdx = remainingProductQuantities.findIndex(
            (rpq) =>
              rpq.productReference === reference &&
              rpq.suborderReference === suborder.reference
          );
          if (productIdx !== -1) {
            remainingProductQuantities[productIdx].remainingQuantity -=
              quantity;
          }
        });
        const remainers = remainingProductQuantities.filter(
          (x) => x.remainingQuantity > 0
        );
        disabled = remainers.length > 0;
        if (disabled) {
          disabledMessage = getPackWithTooMuchProductsMessage(remainers);
        }
      }

      const label = (
        <ReceptionReferenceLabel
          disabledMessage={disabledMessage}
          onEdit={onEdit}
          reception={reception}
          receptionReference={receptionReference}
        />
      );

      return {
        value: toTreeReferenceValue({
          reception,
          productQuantity: null,
          productQuantities,
          receptionReference,
        }),
        children,
        disabled,
        label,
        showCheckbox,
      };
    },
    [edbProducts, integratedOrders, onEdit, showCheckbox, formatProductQuantity]
  );

  const formatProductReference = useCallback(
    (
      reception: Reception,
      productQuantity: ProductQuantity,
      receptionReference: ReceptionReference
    ): JNode | null => {
      let disabled = false;
      let disabledMessage: string;
      if (integratedOrders) {
        const { originalProductReference, suborderReferences } =
          receptionReference;
        const { orderProducts } = integratedOrders;
        const integratedProduct = orderProducts.find((product) => {
          const { order, suborderId, suborderProductId } = product;
          const suborder = order.suborders.find((s) => s.id === suborderId);
          const { reference: suborderRef, suborderProducts } = suborder;
          const productRef = suborderProducts.find(
            (p) => p.id === suborderProductId
          ).reference;
          return (
            productRef === originalProductReference &&
            suborderReferences[0] === suborderRef
          );
        });
        if (!integratedProduct) {
          if (hideNotIntegratedProductReferences) {
            return null;
          }
          disabled = true;
          disabledMessage = "Produit non intégré initialement";
        }
      }

      const label = (
        <ProductQuantityLabel
          disabledMessage={disabledMessage}
          productQuantity={productQuantity}
          receptionReference={receptionReference}
          reception={reception}
          onEdit={onEdit}
        />
      );

      return {
        value: toTreeReferenceValue({
          reception,
          productQuantity,
          productQuantities: [productQuantity],
          receptionReference,
        }),
        children: null,
        disabled,
        label,
        showCheckbox,
      };
    },
    [integratedOrders, hideNotIntegratedProductReferences, onEdit, showCheckbox]
  );

  const formatReceptionReference = useCallback(
    (
      reception: Reception,
      productQuantities: ProductQuantity[],
      receptionReference: ReceptionReference
    ): JNode | null => {
      const { referenceType } = receptionReference;
      const { PRODUCT, ORDER } = ReferenceType;

      if (referenceType === ORDER) {
        const { suborderReferences } = receptionReference;

        if (integratedOrders) {
          // Don't display received quantity not related to the integrated orders
          const { orderProducts } = integratedOrders;
          const suborderRefs = orderProducts
            .flatMap((p) =>
              p.order.suborders.filter((s) => s.id === p.suborderId)
            )
            // remove duplicates
            .filter((v, i, a) => a.findIndex((v2) => v2.id === v.id) === i)
            .map((s) => s.reference);

          const shouldBeDisplayed = suborderRefs.some((ref) =>
            suborderReferences.includes(ref)
          );
          if (!shouldBeDisplayed) {
            return null;
          }
        }

        return formatOrderReference(
          reception,
          productQuantities.filter((pq) =>
            suborderReferences.includes(pq.suborderReference)
          ),
          receptionReference
        );
      }
      if (referenceType === PRODUCT) {
        // PRODUCT references are handled in `formatReceptionForTree` to support
        // payloads where a single receptionReference spans multiple suborders.
        // Returning null here preserves the previous behavior shape for ORDER
        // and prevents accidental double-rendering.
        return null;
      }
      throw new Error(`Unhandled reference type: ${referenceType}`);
    },
    [integratedOrders, formatOrderReference]
  );

  const formatReceptionForTree = useCallback(
    (isReferencingIncomplete: boolean, reception): JNode | null => {
      const { productQuantity, receptionReferences } = reception;

      // When a PRODUCT receptionReference spans multiple suborders (suborderReferences contains > 1),
      // we must map each receptionReference to a single ProductQuantity line to avoid duplicates.
      // We do this by consuming one (productRef, suborderRef) pair per displayed node.
      const consumedByProductRef = new Map<string, Set<string>>();

      const childrenNodes = [
        ...(receptionReferences || [])
          .sort(
            (rr1, rr2) =>
              rr1?.suborderReferences[0] > rr2?.suborderReferences[0]
          )
          .flatMap((receptionReference) => {
            if (receptionReference?.referenceType === ReferenceType.PRODUCT) {
              const originalRef = receptionReference.originalProductReference;
              const suborderRefs = Array.isArray(
                receptionReference.suborderReferences
              )
                ? receptionReference.suborderReferences
                : [];

              const candidates = (productQuantity || [])
                .filter((pq) => pq?.receivedQuantity > 0)
                .filter((pq) => pq?.productId === originalRef)
                .filter((pq) =>
                  suborderRefs.length > 0
                    ? suborderRefs.includes(pq.suborderReference)
                    : true
                )
                .sort((a, b) =>
                  String(a.suborderReference).localeCompare(
                    String(b.suborderReference)
                  )
                );

              if (candidates.length === 0) return [];

              const consumedForRef =
                consumedByProductRef.get(originalRef) ?? new Set<string>();

              const chosen = candidates.find(
                (pq) => !consumedForRef.has(pq.suborderReference)
              );
              if (!chosen) return [];

              consumedForRef.add(chosen.suborderReference);
              consumedByProductRef.set(originalRef, consumedForRef);

              // Clone receptionReference so downstream logic relying on suborderReferences[0]
              // behaves correctly for this specific suborder.
              const rrForSuborder = {
                ...receptionReference,
                suborderReferences: [chosen.suborderReference],
              } as ReceptionReference;

              // Don't display a product already fully tracked
              if (edbProducts) {
                const alreadyTrackedProduct = (edbProducts as any[]).find(
                  (p) => p.receptionReferenceId === rrForSuborder.id
                );
                const trackedQuantity = alreadyTrackedProduct?.qty || 0;
                const remainingReceivedProductQty =
                  chosen.receivedQuantity - trackedQuantity;
                if (remainingReceivedProductQty === 0) {
                  return [];
                }
              }

              const node = formatProductReference(
                reception,
                chosen,
                rrForSuborder
              );
              return node ? [node] : [];
            }

            const node = formatReceptionReference(
              reception,
              productQuantity,
              receptionReference
            );
            return node ? [node] : [];
          }),
      ];
      const children = childrenNodes.length > 0 ? childrenNodes : null;

      // If all reception references were filtered out (e.g., all already tracked),
      // don't display the reception at all
      if (!children && receptionReferences.length > 0) {
        return null;
      }

      const allChildrenDisabled = children?.every((c) => c.disabled);
      const disabledMessage = allChildrenDisabled
        ? getNoCheckableMessage()
        : !children
          ? getNoReceptionReferencesMessage()
          : undefined;

      return {
        value: reception.id,
        disabled:
          (receptionReferences.length === 0 && productQuantity.length === 0) ||
          allChildrenDisabled,
        label: (
          <ReceptionLabel
            disabledMessage={disabledMessage}
            isReferencingIncomplete={isReferencingIncomplete}
            onEdit={onEdit}
            reception={reception}
          />
        ),
        showCheckbox:
          showCheckbox &&
          (!!children || showCheckboxForReceptionWithoutProducts),
        children,
      };
    },
    [
      edbProducts,
      formatProductReference,
      onEdit,
      showCheckbox,
      showCheckboxForReceptionWithoutProducts,
      formatReceptionReference,
    ]
  );

  return { formatReceptionForTree };
};

export default useReceptionForTree;
